import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { ElMessage } from 'element-plus'
import { useAuthStore } from '@/stores/auth'
import router from '@/router'

/**
 * 统一的 axios 实例。真实后端接入后，只需保证接口返回
 * { code: number; message: string; data: T } 结构，其余业务代码无需改动。
 */
const request: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000,
})

/**
 * 从 axios 错误中提取后端返回的错误信息，避免直接显示
 * "Request failed with status code 404" 这类无意义的 40x 提示。
 * 兼容常见后端错误结构：{ message }、{ msg }、{ error }、{ detail } 及字符串响应体。
 */
export function extractServerError(error: unknown): string {
  if (!error || typeof error !== 'object') return ''
  const data = (error as { response?: { data?: unknown } }).response?.data
  if (typeof data === 'string' && data.trim()) return data
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    for (const key of ['message', 'msg', 'error', 'detail']) {
      const value = obj[key]
      if (typeof value === 'string' && value.trim()) return value
    }
  }
  return ''
}

request.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const auth = useAuthStore()
  if (auth.token) {
    config.headers.set('Authorization', `Bearer ${auth.token}`)
  }
  return config
})

request.interceptors.response.use(
  (response) => {
    const payload = response.data
    if (payload && typeof payload === 'object' && 'code' in payload) {
      // 统一约定：后端返回 { code: number; message: string; data: T }，code === 0 表示成功
      if (payload.code !== 0) {
        ElMessage.error(payload.message || '请求失败')
        return Promise.reject(new Error(payload.message || '请求失败'))
      }
      return payload.data
    }
    return payload
  },
  (error) => {
    // 优先使用后端返回的错误信息，避免直接显示 "Request failed with status code 40x"
    const serverMessage = extractServerError(error)
    if (serverMessage) {
      // 附加到 error 上，便于业务层 catch 拿到后端错误信息
      ;(error as { serverMessage?: string }).serverMessage = serverMessage
    }
    if (error.response?.status === 401) {
      const auth = useAuthStore()
      auth.logout()
      router.push('/login')
      ElMessage.error(serverMessage || '登录已失效，请重新登录')
    } else {
      ElMessage.error(serverMessage || error.response?.statusText || error.message || '网络错误，请稍后重试')
    }
    return Promise.reject(error)
  },
)

export default request
